import { Grid } from '@mui/material';

import { useCallback, useEffect, useState } from 'react';

import { useDispatch, useSelector } from 'react-redux';

import { useLocation, useNavigate } from 'react-router-dom';

import Banner from 'common/components/Banner/Banner';
import { TabPanel } from 'common/components/TabBar/TabBar';
import {
	StyledLinedTabs,
	StyledLinedTab,
} from 'common/components/TabBar/TabBarList';
import AlertComponent from 'common/components/ToastMessage/ToastMessage';
import { FeatureFlagsKeysEnum } from 'common/constants/FeatureFlags';
import { rrcpDashboardRoutes } from 'common/constants/rrcp-dashboard-routes';
import localStorageWrapper from 'common/loginUtils/local-storage-wrapper';
import { RootState } from 'common/store/AppStore';
import {
	setHomePageTabIndex,
	setMyApprovalsSelectedTabIndex,
	setToastMessage,
	setSelectedMyApprovalItem,
	setApprovalDrawerState,
	setApprovalDrawerTabState,
} from 'common/store/reducer/commonReducer';
import { urlWithTenant } from 'common/utils/utilFunctions';

import { useFeatureFlag } from 'hooks/useFeatureFlags';

import DashboardBanner from './DashboardBanner';
import MyApprovals from './MyApprovals/MyApprovals';
import { TAB_NAMES } from './MyApprovals/MyApprovalUtils';
import QuickLinks from './QuickLinks/QuickLinks';
import TasksList from './TasksList/TasksList';

import styles from './Dashboard.module.scss';

const Dashboard = () => {
	const dispatch = useDispatch();

	const location = useLocation();
	const navigate = useNavigate();
	const { parent } = rrcpDashboardRoutes;
	const { PENDING_TAB, APPROVED_TAB, REJECTED_TAB, CHANGE_REQUEST } = TAB_NAMES;

	useEffect(() => {
		const { pathname } = location;
		const decodedUri = decodeURIComponent(pathname ?? '');
		const pathSegments = decodedUri.split('/').filter(Boolean);
		if (pathSegments?.length > 2) {
			setSelectedIndex(2);
			const status = pathSegments[3];
			const entityType = pathSegments[4];
			const entityId = pathSegments[5];
			const count = pathSegments[6];
			const itemData = {
				changeCount: count ? parseInt(count, 10) : 0,
				displayId: '',
				id: entityId,
				name: '',
				reviewStatus: '',
				type: entityType,
			};
			if (
				status?.toLowerCase() === PENDING_TAB.toLowerCase() ||
				status?.toLowerCase() === CHANGE_REQUEST.toLowerCase()
			) {
				dispatch(setMyApprovalsSelectedTabIndex(0));
				itemData.reviewStatus = PENDING_TAB;
			} else if (status?.toLowerCase() === APPROVED_TAB.toLowerCase()) {
				dispatch(setMyApprovalsSelectedTabIndex(1));
				itemData.reviewStatus = APPROVED_TAB;
			} else if (status?.toLowerCase() === REJECTED_TAB.toLowerCase()) {
				dispatch(setMyApprovalsSelectedTabIndex(2));
				itemData.reviewStatus = REJECTED_TAB;
			}
			dispatch(setSelectedMyApprovalItem(itemData));
			dispatch(setApprovalDrawerState(true));
			dispatch(setApprovalDrawerTabState(1));
			navigate(urlWithTenant(`${parent}`));
		}
	}, [location]);
	const isLDResponseExists: boolean = useSelector(
		(state: RootState) => state.common.isLDResponseAvailable,
	);

	const { IS_HOME_PAGE_APPROVALS_ENABLED } = FeatureFlagsKeysEnum;

	const isApprovalsEnabled = useFeatureFlag(
		IS_HOME_PAGE_APPROVALS_ENABLED,
		isLDResponseExists,
	);

	const tabIndex = useSelector(
		(state: RootState) => state.common.homePageSelectedTabIndex,
	);

	const { isToastOpen, message, severity } = useSelector(
		(state: RootState) => state.common.toastMessage,
	);

	const [selectedIndex, setSelectedIndex] = useState<number>(tabIndex);

	const onDashboardTabChange = (event: any, id: number) => {
		setSelectedIndex(id);
		dispatch(setHomePageTabIndex(id));
	};

	const handleToast = useCallback(() => {
		dispatch(
			setToastMessage({
				isToastOpen: false,
				message: '',
				severity: '',
			}),
		);
	}, []);

	const tabs = [
		{
			id: 1,
			label: 'Overview',
			name: 'Overview',
			show: true,
			children: <TasksList key="taskslist" />,
		},
		{
			id: 2,
			label: 'Actions',
			name: 'Actions',
			show: true,
			children: <QuickLinks key="quickLinks" />,
		},
		{
			id: 3,
			label: 'My approvals',
			name: 'My approvals',
			show: isApprovalsEnabled,
			children: <MyApprovals key="myapproval" />,
		},
	];

	const enabledTabs = tabs.filter((res) => res.show);
	useEffect(() => {
		if (!localStorageWrapper.getStorageItem('tenantId')) {
			const tenantId = window.location.pathname;
			const tenantUrl = tenantId.split('/')[1];
			localStorageWrapper.setStorageItem('tenantId', tenantUrl);
		}
	}, []);
	return (
		<Grid container>
			<Grid
				container
				xs={12}
				sm={12}
				md={12}
				lg={12}
				xl={12}
				data-testid="dashboard-container"
			>
				<Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
					<Banner>
						<DashboardBanner />{' '}
					</Banner>
				</Grid>

				<Grid
					item
					xs={12}
					sm={12}
					md={12}
					lg={12}
					xl={12}
					className={styles['dashboard-toolbar-grid-container']}
				>
					<div className={styles['dashboard-toolbar-tabs']}>
						<div className={styles['dashboard-toolbar-tabs-block']}>
							<StyledLinedTabs
								data-testid="dashboard-toolbar-maintab"
								value={selectedIndex}
								onChange={onDashboardTabChange}
								aria-label="scrollable prevent tabs example"
							>
								{enabledTabs.map((ele: any) => (
									<StyledLinedTab
										key={ele?.id}
										data-testid="dashboard-toolbar-actions"
										label={ele?.label}
									/>
								))}
							</StyledLinedTabs>
						</div>
					</div>
				</Grid>

				<Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
					{enabledTabs.map((res, i) => (
						<TabPanel
							value={selectedIndex || 0}
							index={i}
							key={`${res.id}-tab-panel-${res.label}`}
						>
							{res.children}
						</TabPanel>
					))}
				</Grid>
			</Grid>
			<AlertComponent
				open={isToastOpen ?? false}
				toastMessage={message ?? ''}
				severity={severity ?? ''}
				handleCloseMessage={handleToast}
				vertical="top"
				horizontal="right"
				data-testid="alert-component"
			/>
		</Grid>
	);
};

export default Dashboard;
